import { useState, useMemo } from 'react';
import {
  Container,
  Box,
  Flex,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useAuth } from '../../../contexts/AuthContext';
import { useIdeas } from './useIdeas';
import { IdeaCard } from './components/IdeaCard';
import { AddIdeaModal } from './components/AddIdeaModal';
import { useUsers } from '../Users/useUsers';
import { AddIcon } from '@chakra-ui/icons';

/**
 * Ideas Page - Track improvement ideas and fixes
 * Migrated from Supabase/Tailwind to Firestore/Chakra UI
 */
export function IdeasPage() {
  const { currentUser, SuperAdmin } = useAuth();
  const toast = useToast();
  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();

  const { ideas, loading, createIdea, updateIdea, deleteIdea } = useIdeas(
    currentUser?.uid
  );
  const { users } = useUsers();

  const [typeFilter, setTypeFilter] = useState<'all' | 'idea' | 'fix'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'completed'>('all');

  const headingColor = useColorModeValue('gray.900', 'white');
  const subheadingColor = useColorModeValue('gray.600', 'gray.400');

  // Filter ideas
  const filteredIdeas = useMemo(() => {
    let filtered = ideas;

    if (typeFilter !== 'all') {
      filtered = filtered.filter((idea) => idea.type === typeFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((idea) => idea.status === statusFilter);
    }

    return filtered;
  }, [ideas, typeFilter, statusFilter]);

  const handleCreateIdea = async (values: {
    title: string;
    description: string;
    type: 'idea' | 'fix';
  }) => {
    try {
      await createIdea(values);
      toast({
        title: 'Thank you for sharing your idea!',
        description: 'We appreciate your contribution to improving our processes.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error creating idea',
        description: (error as Error).message,
        status: 'error',
        duration: 5000,
      });
      throw error;
    }
  };

  const handleComplete = async (ideaId: string) => {
    try {
      await updateIdea(ideaId, { status: 'completed' });
      toast({
        title: 'Idea marked as completed',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Error completing idea',
        description: (error as Error).message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleDelete = async (ideaId: string) => {
    try {
      await deleteIdea(ideaId);
      toast({
        title: 'Idea deleted',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Error deleting idea',
        description: (error as Error).message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text color="gray.500">Loading ideas...</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={8}>
        <Box>
          <Heading size="lg" color={headingColor} mb={2}>
            Ideas & Fixes
          </Heading>
          <Text color={subheadingColor}>
            Track improvement ideas and bug fixes
          </Text>
        </Box>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="blue"
          onClick={onAddOpen}
        >
          Add Idea
        </Button>
      </Flex>

      {/* Filters */}
      <Tabs
        onChange={(index) => {
          const types: ('all' | 'idea' | 'fix')[] = ['all', 'idea', 'fix'];
          setTypeFilter(types[index]);
        }}
        colorScheme="blue"
        mb={6}
      >
        <TabList>
          <Tab>All</Tab>
          <Tab>Ideas</Tab>
          <Tab>Fixes</Tab>
        </TabList>
      </Tabs>

      <Tabs
        onChange={(index) => {
          const statuses: ('all' | 'new' | 'completed')[] = ['all', 'new', 'completed'];
          setStatusFilter(statuses[index]);
        }}
        colorScheme="blue"
        mb={6}
      >
        <TabList>
          <Tab>All Status</Tab>
          <Tab>New</Tab>
          <Tab>Completed</Tab>
        </TabList>
      </Tabs>

      {/* Ideas List */}
      {filteredIdeas.length === 0 ? (
        <Box textAlign="center" py={12}>
          <Text color={subheadingColor} mb={4}>
            No ideas found
          </Text>
          <Button colorScheme="blue" onClick={onAddOpen}>
            Add Your First Idea
          </Button>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {filteredIdeas.map((idea) => (
            <IdeaCard
              key={idea.id}
              idea={idea}
              users={users}
              onComplete={() => handleComplete(idea.id)}
              onDelete={() => handleDelete(idea.id)}
              canDelete={SuperAdmin === true}
            />
          ))}
        </SimpleGrid>
      )}

      {/* Add Idea Modal */}
      <AddIdeaModal
        isOpen={isAddOpen}
        onClose={onAddClose}
        onSubmit={handleCreateIdea}
      />
    </Container>
  );
}

